import moment from 'moment';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { fetchMenuItems } from '../../services/Menu';
import UserContext from '../../../contexts/UserContext';
import sendToast from '../../../utils/Toast';
import { ServicesType } from '../../types/ServiceInstance';
import { Button, Dimmer, Form, Grid, Icon, Loader, Message, Popup, Radio } from 'semantic-ui-react';
import { getAssetById } from '../../services/Asset';
import "./style.less";
import MenuItemCard from './MenuItemCard';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import { ServiceAddOn } from '../../types/Service';
import { cart, customDateTimeFormat } from '../../../utils';
import { createDiningOrder } from '../../services/OrderStatus';
import { MealItem, useCart } from '../../contexts/cart/CartContext';
import CartCarousel from './CartCarousel';
import { cloneDeep } from 'lodash';
import { previewRequestInstanceCharge } from '../../services/Payments';
import useNeighborsPaymentAccess from '../../hooks/useNeighborsPaymentAccess';
import useResidentOrderCardEligibility from '../../hooks/useResidentOrderCardEligibility';
import { isPaymentRequiredForService } from '../../utils/paymentStatus';
import { getCardGateActionLabel, getCardGateMessage, resolveCardGateReason } from '../../utils/orderCardGate';

interface RouteParams {
    restaurantId: string;
    menuId: string;
}
interface LocationState {
    from?: string;
    redirectRestaurantId?: string;
}

const OrderBookingPage = () => {
    const [mealDetails, setMealDetails] = useState<ServicesType>();
    const [mealDetailsLoading, setMealDetailsLoading] = useState<boolean>(false);
    // Tracks only the initial menu/cart fetch, separate from `mealDetailsLoading` (which
    // also covers order submission) — payability resolution must not reopen the card gate
    // while a free order is mid-submit.
    const [mealDataFetching, setMealDataFetching] = useState<boolean>(false);
    const [restaurantName, setRestaurantName] = useState<string>("");
    const [selectedDeliveryOption, setSelectedDeliveryOption] = useState<string>('');
    const [singleItemSelectedAddon, setSingleItemSelectedAddon] = useState<ServiceAddOn[]>([]);
    const [totalPrice, setTotalPrice] = useState<number>(0);
    const [cartItems, setCartItems] = useState<ServicesType[]>([]);
    const [SpecialNotes, setSpecialNotes] = useState<string>('');
    const [SingleItemQuantity, setSingleItemQuantity] = useState<number>(1);
    const [fetchCartItemsFlag, setFetchCartItemsFlag] = useState<boolean>(false);
    const [paymentConfirmOpen, setPaymentConfirmOpen] = useState<boolean>(false);
    const [pendingPaymentAmountCents, setPendingPaymentAmountCents] = useState<number>(0);
    const [pendingMealItems, setPendingMealItems] = useState<MealItem[] | null>(null);
    const [confirmingPayment, setConfirmingPayment] = useState<boolean>(false);
    // The menu docs that say which items are payable did not load. With payments on the
    // order stays blocked until a retry loads them, instead of pricing the order as free.
    const [menuLoadFailed, setMenuLoadFailed] = useState<boolean>(false);
    const [cartItemsLoaded, setCartItemsLoaded] = useState<boolean>(false);
    // Blocks a second Continue on the payment confirm before `confirmingPayment` re-renders.
    const isConfirmingRef = useRef(false);
    // Guards against a second submit firing while the first is still in flight -- the Send
    // button's `disabled` only reflects the card gate, not `mealDetailsLoading`, so a fast
    // double-click can re-enter before the loading state's re-render lands. A ref (not
    // state) so the check is synchronous and can't race a pending re-render.
    const isSubmittingRef = useRef(false);
    const { restaurantId, menuId } = useParams<RouteParams>();
    const history = useHistory();
    const location = useLocation<LocationState>();
    const {
        userObject: {
            registrants: { list: listOfResidents, activeRegistrantsIndexes },
        },
    } = useContext(UserContext);
    const activeResident = listOfResidents[activeRegistrantsIndexes[0]];
    const { mealItems, clearCart, removeFromCart } = useCart()
    const {
        resolvedFacilityPaymentsEnabled,
        facilityLookupFailed,
        retry: retryFacilityPayments,
    } = useNeighborsPaymentAccess();
    const {
        loading: cardEligibilityLoading,
        hasDefaultCard,
        hasAnyCard,
        defaultCardExpired,
        lookupFailed,
        refresh,
    } = useResidentOrderCardEligibility({
        residentId: String(activeResident?._id || ''),
        facilityId: String(activeResident?.Facility || ''),
        enabled: resolvedFacilityPaymentsEnabled,
    });

    // `resolved` guards the window where the menu docs that carry `defaults.isPaymentRequired`
    // have not loaded yet — without it a payable order momentarily looks free and the gate lifts.
    // It is keyed off the in-flight fetch rather than "every cart item matched" so an item that
    // is no longer served cannot leave the gate stuck closed.
    const orderPayability = useMemo(() => {
        if (!resolvedFacilityPaymentsEnabled) return { resolved: true, payable: false };
        if (mealDataFetching) return { resolved: false, payable: false };
        if (menuLoadFailed) return { resolved: true, payable: false };
        if (menuId && restaurantId) {
            return { resolved: Boolean(mealDetails), payable: isPaymentRequiredForService(mealDetails) };
        }

        return {
            resolved: true,
            payable: mealItems.some((mealItem) => {
                const cartItem = cartItems.find((item) => String(item._id) === String(mealItem.serviceInstanceId));
                return isPaymentRequiredForService(cartItem);
            }),
        };
    }, [resolvedFacilityPaymentsEnabled, mealDataFetching, menuLoadFailed, menuId, restaurantId, mealDetails, mealItems, cartItems]);

    const orderContainsPayableItems = orderPayability.payable;
    const cardGateReason = (orderContainsPayableItems || !orderPayability.resolved)
        ? resolveCardGateReason({
            loading: cardEligibilityLoading || !orderPayability.resolved,
            lookupFailed: lookupFailed || facilityLookupFailed,
            hasDefaultCard,
            hasAnyCard,
            defaultCardExpired,
        })
        : null;
    const isMenuGateBlocked = resolvedFacilityPaymentsEnabled && menuLoadFailed && !mealDataFetching;
    // Cart items today's menu did not return (taken off the menu, or left in the cart from another
    // day) have an unknown price, so with payments on they block Send until the resident removes them.
    const unavailableCartItems = useMemo(() => {
        if ((menuId && restaurantId) || !cartItemsLoaded) return [];
        const returnedIds = new Set((cartItems || []).map((meal) => String(meal._id)));
        return mealItems.filter((item) => !returnedIds.has(String(item.serviceInstanceId)));
    }, [menuId, restaurantId, cartItemsLoaded, cartItems, mealItems]);
    const isUnavailableItemsGateBlocked = resolvedFacilityPaymentsEnabled && unavailableCartItems.length > 0 && !mealDataFetching;
    const isCardGateBlocked = Boolean(cardGateReason) || isMenuGateBlocked || isUnavailableItemsGateBlocked;
    const cardGateHeading = cardGateReason === 'no_card' || cardGateReason === 'no_default' || cardGateReason === 'default_expired'
        ? 'Payment Method Required'
        : cardGateReason === 'lookup_failed'
            ? 'Could Not Verify Your Saved Card'
            : 'Checking Your Saved Card';

    useEffect(() => {
        // if we have menuId and restaurantId then we will fetch only one menu item as it is created from menu page else we will fetch all the items from the cart
        if (menuId && restaurantId) {
            fetchOneMenu();
        } else {
            !fetchCartItemsFlag && fetchCartItems();
        }
    }, [mealItems]);
    useEffect(() => {
        calculateTotalPrice();
    }, [mealDetails, singleItemSelectedAddon, SingleItemQuantity, cartItems, mealItems]);

    const fetchOneMenu = async () => {
        try {
            setMealDetailsLoading(true);
            setMealDataFetching(true);
            const filter = {
                Facility: activeResident.Facility,
                startDate: moment(new Date()).startOf('day').format(customDateTimeFormat),
                endDate: moment(new Date()).endOf('day').format(customDateTimeFormat),
                _id: menuId,
                source: 'Menu',
            };
            const [selectedMeal, restaurant] = await Promise.all([
                fetchMenuItems({ filter }),
                getAssetById({ Facility: activeResident.Facility, AssetId: String(restaurantId) }),
            ]);
            setMealDetails(selectedMeal[0]);
            setRestaurantName(restaurant.response.AssetName);
            // No doc back means the price and payment setting for this item are unknown.
            setMenuLoadFailed(!selectedMeal[0]);
        } catch (error) {
            setMenuLoadFailed(true);
            sendToast('error', error instanceof Error ? error.message : 'Error fetching meal details');
        } finally {
            setMealDetailsLoading(false);
            setMealDataFetching(false);
        }
    };
    const fetchCartItems = async () => {
        if (mealItems.length === 0) {
            return;
        }
        try {
            setMealDetailsLoading(true);
            setMealDataFetching(true);
            setFetchCartItemsFlag(true);
            const _ids = mealItems.map((item) => item.serviceInstanceId);
            const filter = {
                Facility: activeResident.Facility,
                startDate: moment(new Date()).startOf('day').format(customDateTimeFormat),
                endDate: moment(new Date()).endOf('day').format(customDateTimeFormat),
                _ids: _ids,
                source: 'Menu',
            };
            const response = await fetchMenuItems({ filter });
            setCartItems(response);
            setCartItemsLoaded(true);
            setMenuLoadFailed(false);
        } catch (error) {
            setMenuLoadFailed(true);
            sendToast('error', 'Failed to fetch cart Details.');
        } finally {
            setMealDetailsLoading(false);
            setMealDataFetching(false);
        }
    };

    const calculateTotalPrice = () => {
        // if we have menuId and restaurantId then we will calculate the total price for only one item else we will calculate the total price for all the items in the cart.
        let total = 0;
        if (mealDetails && restaurantId && menuId) {
            total =
                mealDetails.defaults && mealDetails.defaults.externalCost
                    ? Number(mealDetails.defaults.externalCost)
                    : 0;
            if (singleItemSelectedAddon) {
                singleItemSelectedAddon.forEach((addOn) => {
                    total += Number(addOn.itemCost);
                });
            }
            total *= SingleItemQuantity;
        } else {
            mealItems && mealItems.length > 0 && mealItems.forEach((item) => {
                const meal = cartItems.find((cartItem) => String(cartItem._id) === item.serviceInstanceId);
                if (meal) {
                    let lineTotal = meal.defaults && meal.defaults.externalCost ? Number(meal.defaults.externalCost) : 0;
                    if (item.serviceAddOns && item.serviceAddOns.length > 0) {
                        item.serviceAddOns.forEach((addOn: ServiceAddOn) => {
                            lineTotal += Number(addOn.itemCost);
                        });
                    }
                    total += lineTotal * item.quantity;
                }
            }
            );
        }
        setTotalPrice(total);
    };
    
    const handleRedirectToMealSelection = () => {
        const fromPage = location.state && location.state.from;
        const redirectRestaurantId = location.state && location.state.redirectRestaurantId;
        if (fromPage === 'menu') {
            history.goBack();
        } else if (restaurantId || redirectRestaurantId) {
            history.push({
                pathname: `/dining/${restaurantId || redirectRestaurantId}`,
            });
        } else {
            history.push({
                pathname: `/dining`,
            });
        }
    };

    const handleChange = (
        e: React.FormEvent<HTMLInputElement>,
        { value }: any
    ) => {
        setSelectedDeliveryOption(value);
    };

    const handleSubmit = async () => {
        if (isSubmittingRef.current) return;
        if (isCardGateBlocked) {
            if (cardGateReason) {
                sendToast('warn', getCardGateMessage(cardGateReason));
            }
            return;
        }

        isSubmittingRef.current = true;
        try {
            setMealDetailsLoading(true);
            if (!selectedDeliveryOption || !activeResident.Facility || !activeResident._id || (restaurantId && !menuId)) {
                throw new Error('Please select delivery option');
            }
            let mealItemsArr: MealItem[] = [];
            if (menuId && restaurantId) { // when we will create the order from menu page mealItems will be only one Single Item
                mealItemsArr = [
                    {
                        serviceInstanceId: menuId,
                        serviceAddOns: singleItemSelectedAddon,
                        quantity: SingleItemQuantity,
                    },
                ];
            } else { // when we will create the order from Dining page
                mealItemsArr = cloneDeep(mealItems);
            }

            const normalizedMealItemsArr = mealItemsArr.map((item) => ({
                ...item,
                serviceAddOns: Array.isArray(item.serviceAddOns) ? item.serviceAddOns : [],
                quantity: Number.isFinite(Number(item.quantity)) && Number(item.quantity) > 0
                    ? Math.max(1, Math.floor(Number(item.quantity)))
                    : 1,
            }));

            let mealItemsWithPaymentConsent = normalizedMealItemsArr;
            if (resolvedFacilityPaymentsEnabled && orderContainsPayableItems) {
                const consentTimestamp = Date.now();
                const previewResults = await Promise.all(normalizedMealItemsArr.map(async (item) => {
                    const preview = await previewRequestInstanceCharge({
                        facilityId: String(activeResident.Facility),
                        residentId: String(activeResident._id),
                        originalServiceInstanceId: String(item.serviceInstanceId),
                        serviceAddOns: (item.serviceAddOns || []).map((addOn) => ({ id: String(addOn.id) })),
                        quantity: item.quantity,
                    });
                    return { item, preview };
                }));

                let aggregatedAmountCents = 0;
                mealItemsWithPaymentConsent = previewResults.map(({ item, preview }) => {
                    if (!preview || !preview.requiresPayment) {
                        return item;
                    }
                    const amountCents = Number(preview.chargeBreakdown && preview.chargeBreakdown.totalCents);
                    if (!Number.isFinite(amountCents) || amountCents < 0) {
                        throw new Error('Unable to preview payment amount for one or more menu items');
                    }
                    aggregatedAmountCents += Math.round(amountCents);
                    return {
                        ...item,
                        paymentConsent: {
                            amountDisplayedCents: Math.round(amountCents),
                            confirmedAt: consentTimestamp,
                            source: 'neighbors_dining',
                        },
                    };
                });

                if (aggregatedAmountCents > 0) {
                    setPendingMealItems(mealItemsWithPaymentConsent);
                    setPendingPaymentAmountCents(aggregatedAmountCents);
                    setPaymentConfirmOpen(true);
                    return;
                }
            }

            await submitOrder(mealItemsWithPaymentConsent);
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Error while placing order');
        } finally {
            setMealDetailsLoading(false);
            isSubmittingRef.current = false;
        }
    };

    const submitOrder = async (mealItemsWithPaymentConsent: MealItem[]) => {
        const orderData = {
            Facility: activeResident.Facility,
            ResidentId: String(activeResident._id),
            specialNotes: SpecialNotes,
            orderType: selectedDeliveryOption,
            mealItems: mealItemsWithPaymentConsent,
        };
        const response = await createDiningOrder(activeResident.Facility, orderData);
        if (response) {
            sendToast('success', 'Order created successfully');
            handleRedirectToMealSelection();
            if (!menuId && !restaurantId) { // we will only clear the cart when it is order is created from dining page
                clearCart()
            }
        } else {
            throw new Error('Error while placing order.');
        }
    };

    const handlePaymentConfirm = async () => {
        if (isConfirmingRef.current) return;
        if (!pendingMealItems) {
            setPaymentConfirmOpen(false);
            return;
        }
        isConfirmingRef.current = true;
        try {
            setConfirmingPayment(true);
            // The agreed amount is recorded at the moment the resident presses Continue.
            const confirmedAt = Date.now();
            await submitOrder(pendingMealItems.map((item) => (item.paymentConsent
                ? { ...item, paymentConsent: { ...item.paymentConsent, confirmedAt } }
                : item)));
            setPaymentConfirmOpen(false);
            setPendingMealItems(null);
            setPendingPaymentAmountCents(0);
        } catch (error) {
            // Keep the order and amount in the dialog so Continue retries it.
            sendToast('error', error instanceof Error ? error.message : 'Error while placing order');
        } finally {
            setConfirmingPayment(false);
            isConfirmingRef.current = false;
        }
    };

    const handlePaymentCancel = () => {
        setPaymentConfirmOpen(false);
        setPendingMealItems(null);
        setPendingPaymentAmountCents(0);
    };

    const handleRetryCardEligibility = async () => {
        if (facilityLookupFailed) retryFacilityPayments();
        await refresh();
    };

    const handleRemoveUnavailableItems = () => {
        unavailableCartItems.forEach((item) => removeFromCart(item.serviceInstanceId));
    };

    const handleRetryMenu = async () => {
        if (menuId && restaurantId) {
            await fetchOneMenu();
        } else {
            await fetchCartItems();
        }
        await refresh();
    };

    return <div>
        <Dimmer inverted active={mealDetailsLoading}>
            <Loader>Loading</Loader>
        </Dimmer>
        {/* `warning` is required: semantic-ui hides `.ui.form .warning.message` unless the form carries the matching state class */}
        <Form warning={isCardGateBlocked}>
            <div className="headingContainer">
                <Popup
                    content="Go back to choose meal"
                    trigger={
                        <Icon
                            name="arrow left"
                            size="large"
                            className="backIcon"
                            onClick={handleRedirectToMealSelection}
                        />
                    }
                />
                <div className="mainHeadingContainer">
                    <h1 className="heading">Place your order</h1>
                </div>
            </div>
            <hr className="spacing" />
            {
                !menuId && !restaurantId &&
                <CartCarousel
                    mealDetailsLoading={mealDetailsLoading}
                    cartItems={cartItems}
                    setCartItems={setCartItems}                    
                />
            }
            <div className='center-self-column'>
                <div style={{ width: "30%" }}>
                    {restaurantName &&
                        <div className='nameCont'>
                            <div className="nameContainerOrder" >
                                <p className="diningName">{restaurantName}</p>
                            </div>
                        </div>
                    }
                    <div className='center-self top-ten'>
                        {
                            mealDetails &&
                            <MenuItemCard
                                menuItem={mealDetails}
                                source={cart}
                                setSingleItemSelectedAddon={setSingleItemSelectedAddon}
                                setSingleItemQuantity={setSingleItemQuantity}
                                SingleItemQuantity={SingleItemQuantity}
                                handleRedirectToMealSelection={handleRedirectToMealSelection}
                            />
                        }
                    </div>
                    <hr className="spacing-half" />
                    <Grid columns={2} style={{ fontSize: "20px" }}>
                        <Grid.Column width={7}>
                            {resolvedFacilityPaymentsEnabled && orderContainsPayableItems ? 'Subtotal (before tax):' : 'Total:'}
                        </Grid.Column>
                        <Grid.Column width={9}>
                            ${totalPrice}
                        </Grid.Column>
                    </Grid>
                    {resolvedFacilityPaymentsEnabled && orderContainsPayableItems && (
                        <div className="serviceCardPaymentMeta">
                            <span className="serviceCardPaymentFlag required">Payment Required</span>
                        </div>
                    )}
                    {isMenuGateBlocked && (
                        <Message warning className="orderCardGateNotice">
                            <Message.Header>Could Not Load The Menu</Message.Header>
                            <p>We could not load the menu details needed to price this order. Retry to load them again.</p>
                            <div className="orderCardGateActions">
                                <Button size="small" className="dining-btn-ghost" type="button" onClick={handleRetryMenu} loading={mealDataFetching} disabled={mealDataFetching}>
                                    Retry
                                </Button>
                            </div>
                        </Message>
                    )}
                    {!isMenuGateBlocked && isUnavailableItemsGateBlocked && (
                        <Message warning className="orderCardGateNotice">
                            <Message.Header>Some Items Are No Longer Available</Message.Header>
                            <p>
                                {unavailableCartItems.length === 1
                                    ? "1 item in your cart is not on today's menu. Remove it to place this order."
                                    : `${unavailableCartItems.length} items in your cart are not on today's menu. Remove them to place this order.`}
                            </p>
                            <div className="orderCardGateActions">
                                <Button size="small" className="dining-btn-primary" type="button" onClick={handleRemoveUnavailableItems}>
                                    Remove Unavailable Items
                                </Button>
                            </div>
                        </Message>
                    )}
                    {!isMenuGateBlocked && !isUnavailableItemsGateBlocked && cardGateReason && (
                        <Message warning className="orderCardGateNotice">
                            <Message.Header>{cardGateHeading}</Message.Header>
                            <p>{getCardGateMessage(cardGateReason)}</p>
                            <div className="orderCardGateActions">
                                {cardGateReason !== 'checking' && (
                                    <Button size="small" className="dining-btn-primary" type="button" onClick={() => history.push('/payments')}>
                                        {getCardGateActionLabel(cardGateReason)}
                                    </Button>
                                )}
                                <Button size="small" className="dining-btn-ghost" type="button" onClick={handleRetryCardEligibility} loading={cardEligibilityLoading} disabled={cardEligibilityLoading}>
                                    Retry
                                </Button>
                            </div>
                        </Message>
                    )}
                    <Form.TextArea
                        placeholder='Special Notes:'
                        style={{ width: "100%", marginTop: "20px", padding: "5px" }}
                        value={SpecialNotes}
                        onChange={(e) => setSpecialNotes((e.target as HTMLTextAreaElement).value)}
                    />
                    <Form.Field>
                        <Radio
                            label='Delivery'
                            name='orderType'
                            value='delivery'
                            checked={selectedDeliveryOption === 'delivery'}
                            onChange={handleChange}
                        />
                    </Form.Field>
                    <Form.Field>
                        <Radio
                            label='Pre order for pick up'
                            name='orderType'
                            value='pickUp'
                            checked={selectedDeliveryOption === 'pickUp'}
                            onChange={handleChange}
                        />
                    </Form.Field>
                    <Form.Field>
                        <Radio
                            label='Pre order for dine in'
                            name='orderType'
                            value='dineIn'
                            checked={selectedDeliveryOption === 'dineIn'}
                            onChange={handleChange}
                        />
                    </Form.Field>
                    <div className='center-self'>
                        <Form.Group>
                            <Form.Button className="dining-btn-primary" type="button" onClick={handleSubmit} disabled={isCardGateBlocked}>Send</Form.Button>
                            <Form.Button className="dining-btn-ghost" type="button" onClick={handleRedirectToMealSelection}>Cancel</Form.Button>
                        </Form.Group>
                    </div>
                </div>
            </div>
        </Form>
        <DeleteConfirmationModal
            open={paymentConfirmOpen}
            onClose={handlePaymentCancel}
            onConfirm={handlePaymentConfirm}
            loading={confirmingPayment}
            headerText="Confirm Payment"
            headerIcon="credit card"
            confirmText="Continue"
            confirmColor="blue"
            messageToDisplay={`This order requires payment. Estimated charge is $${(pendingPaymentAmountCents / 100).toFixed(2)}. Continue?`}
        />
    </div>;
};

export default OrderBookingPage;
